import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

/**
 * What each affiliate is actually driving.
 *
 * One row per affiliate, following a single visitor all the way through:
 * they click a link, some of them are distinct people, some of those buy, and
 * each purchase owes the affiliate 10% of the goods. Every figure here is
 * measured — clicks come from recorded landings, orders from paid Stripe
 * sessions, commission from the ledger rows those orders created.
 *
 * Money is shown twice on purpose. "Revenue" is what the customer paid;
 * "commissionable" is that minus tax and shipping, and is what the 10% is
 * taken on. Showing only the first invites the question of why the commission
 * does not match, every single time.
 */

interface AffiliateRow {
  affiliateId: string;
  name: string;
  email: string;
  affiliateCode: string;
  referralLink: string;
  commissionRate: number;
  isActive: boolean;
  discountCode: string | null;
  discountAmount: number;
  clicks: number;
  uniqueVisitors: number;
  signups: number;
  orders: number;
  grossRevenue: number;
  commissionableRevenue: number;
  discountGiven: number;
  pendingCommission: number;
  paidCommission: number;
  conversionRate: number | null;
  lastClickAt: string | null;
}

interface PerformanceTotals {
  affiliates: number;
  activeAffiliates: number;
  clicks: number;
  uniqueVisitors: number;
  signups: number;
  orders: number;
  grossRevenue: number;
  commissionableRevenue: number;
  discountGiven: number;
  pendingCommission: number;
  paidCommission: number;
}

interface PerformanceReport {
  windowDays: number;
  totals: PerformanceTotals;
  affiliates: AffiliateRow[];
}

interface CommissionRow {
  _id: string;
  affiliateId: { _id: string; name: string; affiliateCode: string; walletAddress?: string } | null;
  affiliateCode: string;
  confirmationNumber: string | null;
  commissionableAmount: number;
  orderTotal: number;
  rate: number;
  amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'void';
  createdAt: string;
}

const WINDOWS = [7, 30, 90];

const money = (minorUnits: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((minorUnits || 0) / 100);

const AffiliatePerformance: React.FC = () => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<PerformanceReport | null>(null);
  const [commissions, setCommissions] = useState<CommissionRow[]>([]);
  const [outstanding, setOutstanding] = useState({ amount: 0, orders: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which row is mid-request, so its buttons can be disabled. A double-click
  // on "Mark paid" is refused by the server anyway, but the second click
  // should not look like it worked either.
  const [settling, setSettling] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Which affiliate's "create a code" field is open, and what has been typed
  // into it. Opened rather than acting on one click, because a code cannot be
  // reissued once it exists — the admin gets one chance to choose it, so they
  // are given the chance rather than handed a derived code and locked in.
  const [codingFor, setCodingFor] = useState<string | null>(null);
  const [newCode, setNewCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Requested together: the ledger below is the actionable half of the
      // table above, and one failing should not blank both.
      const [performance, ledger] = await Promise.all([
        apiService.getAffiliatePerformance(days),
        apiService.getAffiliateCommissions({ status: 'pending', limit: 50 }),
      ]);

      if (performance.success) {
        setReport((performance as { report?: PerformanceReport }).report ?? null);
      } else {
        setError(performance.message || 'Could not load affiliate performance');
      }

      if (ledger.success) {
        const data = (ledger as { data?: { commissions: CommissionRow[]; outstanding: typeof outstanding } }).data;
        setCommissions(data?.commissions ?? []);
        setOutstanding(data?.outstanding ?? { amount: 0, orders: 0 });
      }
    } catch (err) {
      console.error('Affiliate performance failed to load:', err);
      setError('Could not reach the server for affiliate performance');
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const settle = async (commissionId: string, status: 'paid' | 'void') => {
    setSettling(commissionId);
    setNotice(null);

    try {
      const result = await apiService.updateAffiliateCommission(commissionId, status);

      if (result.success) {
        setNotice(`Commission marked ${status}.`);
        // Reloaded rather than patched in place: settling moves the
        // affiliate's balance in the table above too, and a half-updated view
        // of who is owed what is worse than a moment's wait.
        await load();
      } else {
        setNotice(result.message || `Could not mark the commission ${status}.`);
      }
    } catch {
      setNotice('Could not reach the server.');
    } finally {
      setSettling(null);
    }
  };

  const createDiscount = async (affiliateId: string) => {
    setSavingCode(true);
    setNotice(null);

    try {
      const result = await apiService.createAffiliateDiscount(affiliateId, newCode.trim() || undefined);

      if (result.success) {
        setNotice(`Discount code ${result.data?.discountCode} created.`);
        setCodingFor(null);
        setNewCode('');
        // Reloaded rather than patched in: the code belongs on the row above
        // and a half-updated table is worse than a moment's wait.
        await load();
      } else {
        // 409 lands here — the code is taken, or they already have one.
        setNotice(result.message || 'Could not create the discount code.');
      }
    } catch {
      setNotice('Could not reach the server.');
    } finally {
      setSavingCode(false);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setNotice('Referral link copied.');
    } catch {
      setNotice('Could not copy the link.');
    }
  };

  const totals = report?.totals;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          {WINDOWS.map((window) => (
            <button
              key={window}
              onClick={() => setDays(window)}
              className={`px-4 py-2 rounded-xl font-bold transition-colors ${
                days === window
                  ? 'bg-brand-blue text-white'
                  : 'bg-white text-deep-navy border border-ice-teal-light hover:border-brand-blue'
              }`}
            >
              {window} days
            </button>
          ))}
        </div>
        <button onClick={load} className="btn-secondary">Refresh</button>
      </div>

      {error && (
        <div className="bg-brand-red/10 border border-brand-red/30 rounded-xl p-4">
          <p className="text-brand-red font-body">{error}</p>
        </div>
      )}

      {notice && (
        <div className="bg-ice-pale border border-ice-teal-light rounded-xl p-4">
          <p className="text-deep-navy font-body">{notice}</p>
        </div>
      )}

      {isLoading && (
        <div className="bg-ice-pale border border-ice-teal-light rounded-xl p-4 text-center">
          <p className="text-deep-navy/70 font-body">Loading affiliate performance…</p>
        </div>
      )}

      {totals && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Link Views</h3>
            <div className="text-3xl font-bold text-brand-blue">{totals.clicks}</div>
            <p className="text-gray-600 text-sm">
              {totals.uniqueVisitors} unique visitor{totals.uniqueVisitors === 1 ? '' : 's'}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Orders</h3>
            <div className="text-3xl font-bold text-brand-blue">{totals.orders}</div>
            <p className="text-gray-600 text-sm">
              {money(totals.grossRevenue)} paid
              {totals.discountGiven > 0 && ` · ${money(totals.discountGiven)} discounted`}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Owed</h3>
            <div className="text-3xl font-bold text-brand-blue">{money(totals.pendingCommission)}</div>
            <p className="text-gray-600 text-sm">Awaiting payout</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Paid Out</h3>
            <div className="text-3xl font-bold text-brand-blue">{money(totals.paidCommission)}</div>
            <p className="text-gray-600 text-sm">
              {totals.activeAffiliates} of {totals.affiliates} affiliate{totals.affiliates === 1 ? '' : 's'} active
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-baseline mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">By affiliate</h3>
          <p className="text-gray-600 font-body">Last {report?.windowDays ?? days} days</p>
        </div>

        {!report?.affiliates.length ? (
          <p className="text-gray-500 font-body">
            No affiliates yet. Create one under Sales &amp; Transactions, then share the
            <span className="font-mono"> /join/&lt;code&gt; </span>
            link — views are counted from the moment someone opens it.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ice-teal-light text-left">
                  <th className="py-2 text-deep-navy font-bold">Affiliate</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Views</th>
                  <th className="py-2 text-deep-navy font-bold text-right">People</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Orders</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Conv.</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Revenue</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Commissionable</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Discounts</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Owed</th>
                  <th className="py-2 text-deep-navy font-bold"></th>
                </tr>
              </thead>
              <tbody>
                {report.affiliates.map((row) => (
                  <tr key={row.affiliateId} className="border-b border-ice-teal-light/50">
                    <td className="py-3">
                      <div className="font-bold text-deep-navy">
                        {row.name}
                        {!row.isActive && (
                          <span className="ml-2 text-xs font-normal text-brand-red">inactive</span>
                        )}
                      </div>
                      <div className="font-mono text-xs text-deep-navy/50">
                        {row.affiliateCode} · {Math.round(row.commissionRate * 100)}%
                      </div>
                      {row.discountCode && (
                        <div className="font-mono text-xs text-brand-blue">
                          {row.discountCode} · {money(row.discountAmount)} off
                        </div>
                      )}
                    </td>
                    <td className="py-3 text-right text-deep-navy/80">{row.clicks}</td>
                    <td className="py-3 text-right text-deep-navy/80">{row.uniqueVisitors}</td>
                    <td className="py-3 text-right text-deep-navy/80">{row.orders}</td>
                    <td className="py-3 text-right text-deep-navy/80">
                      {/* An em dash, not 0% — no clicks means unknown, and 0%
                          would read as "this link fails". */}
                      {row.conversionRate === null ? '—' : `${row.conversionRate}%`}
                    </td>
                    <td className="py-3 text-right text-deep-navy/80">{money(row.grossRevenue)}</td>
                    <td className="py-3 text-right text-deep-navy/80">{money(row.commissionableRevenue)}</td>
                    {/* What their code gave away. Sits next to the commission
                        because the two together are what this affiliate costs;
                        the commission alone understates it. */}
                    <td className="py-3 text-right text-deep-navy/80">
                      {row.discountGiven > 0 ? money(row.discountGiven) : '—'}
                    </td>
                    <td className="py-3 text-right font-bold text-deep-navy">
                      {money(row.pendingCommission)}
                      {row.paidCommission > 0 && (
                        <div className="text-xs font-normal text-deep-navy/50">
                          {money(row.paidCommission)} paid
                        </div>
                      )}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => copyLink(row.referralLink)}
                        className="text-brand-blue hover:text-brand-blue-dark text-sm font-bold"
                      >
                        Copy link
                      </button>

                      {/* Only offered where there is no code. An affiliate who
                          has one cannot be reissued another, so showing the
                          action there would be a button that only ever errors. */}
                      {!row.discountCode && codingFor !== row.affiliateId && (
                        <button
                          onClick={() => { setCodingFor(row.affiliateId); setNewCode(''); setNotice(null); }}
                          className="ml-3 text-brand-blue hover:text-brand-blue-dark text-sm font-bold"
                        >
                          Create code
                        </button>
                      )}

                      {codingFor === row.affiliateId && (
                        <div className="mt-2 flex items-center justify-end space-x-2">
                          <input
                            type="text"
                            value={newCode}
                            onChange={(event) => setNewCode(event.target.value.toUpperCase())}
                            placeholder="Blank to use their name"
                            maxLength={20}
                            disabled={savingCode}
                            className="w-48 px-2 py-1 border border-ice-teal-light rounded-lg font-mono text-sm text-deep-navy focus:outline-none focus:border-brand-blue"
                          />
                          <button
                            onClick={() => createDiscount(row.affiliateId)}
                            disabled={savingCode}
                            className="bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-50 text-white text-sm font-bold px-3 py-1 rounded-lg transition-colors"
                          >
                            {savingCode ? 'Saving…' : 'Create'}
                          </button>
                          <button
                            onClick={() => { setCodingFor(null); setNewCode(''); }}
                            disabled={savingCode}
                            className="text-deep-navy/60 hover:text-deep-navy disabled:opacity-50 text-sm font-bold"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* The payout worklist. Separate from the table above because settling
          is an action on one order, not on an affiliate's total. */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-baseline mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Commissions awaiting payout</h3>
          <p className="text-gray-600 font-body">
            {outstanding.orders} order{outstanding.orders === 1 ? '' : 's'} · {money(outstanding.amount)}
          </p>
        </div>

        {!commissions.length ? (
          <p className="text-gray-500 font-body">
            Nothing outstanding. A commission appears here the moment an affiliate-attributed
            order is paid, and stays until you mark it paid.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ice-teal-light text-left">
                  <th className="py-2 text-deep-navy font-bold">Order</th>
                  <th className="py-2 text-deep-navy font-bold">Affiliate</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Goods</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Commission</th>
                  <th className="py-2 text-deep-navy font-bold">Earned</th>
                  <th className="py-2 text-deep-navy font-bold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((commission) => (
                  <tr key={commission._id} className="border-b border-ice-teal-light/50">
                    <td className="py-3 font-mono text-sm text-deep-navy">
                      {commission.confirmationNumber || '—'}
                    </td>
                    <td className="py-3 text-deep-navy/80">
                      <div>{commission.affiliateId?.name || commission.affiliateCode}</div>
                      {commission.affiliateId?.walletAddress && (
                        <div className="font-mono text-xs text-deep-navy/40">
                          {commission.affiliateId.walletAddress.slice(0, 6)}…
                          {commission.affiliateId.walletAddress.slice(-4)}
                        </div>
                      )}
                    </td>
                    <td className="py-3 text-right text-deep-navy/80">
                      {money(commission.commissionableAmount, commission.currency)}
                      <div className="text-xs text-deep-navy/40">
                        of {money(commission.orderTotal, commission.currency)} paid
                      </div>
                    </td>
                    <td className="py-3 text-right font-bold text-deep-navy">
                      {money(commission.amount, commission.currency)}
                      <div className="text-xs font-normal text-deep-navy/40">
                        {Math.round(commission.rate * 100)}%
                      </div>
                    </td>
                    <td className="py-3 text-deep-navy/60 text-sm">
                      {new Date(commission.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => settle(commission._id, 'paid')}
                        disabled={settling === commission._id}
                        className="bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-50 text-white text-sm font-bold px-3 py-1 rounded-lg transition-colors"
                      >
                        {settling === commission._id ? 'Saving…' : 'Mark paid'}
                      </button>
                      <button
                        onClick={() => settle(commission._id, 'void')}
                        disabled={settling === commission._id}
                        className="ml-2 text-brand-red hover:underline disabled:opacity-50 text-sm font-bold"
                      >
                        Void
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatePerformance;
